/**
 * Machine History Manager - Undo/Redo for Graph Edits
 * 
 * Provides immutable history tracking for state machine changes,
 * allowing users to undo/redo visual edits on the graph.
 * 
 * Design decisions:
 * - History is stored as snapshots of the full machine IR
 * - Max history depth to prevent memory issues
 * - Local storage persistence for session recovery
 */

const MAX_HISTORY_SIZE = 50;
const STORAGE_KEY = 'smf_machine_history';

export class MachineHistoryManager {
  constructor(machineId) {
    this.machineId = machineId;
    this.history = [];
    this.currentIndex = -1;
    this.lastSavedIndex = -1;
    
    // Try to restore from localStorage
    this.restore();
  }

  /**
   * Push a new state to history
   * @param {Object} machineIR - The current machine IR state
   * @param {string} description - Human-readable description of the change
   */
  push(machineIR, description = 'Edit') {
    // If we're not at the end of history, truncate future states
    if (this.currentIndex < this.history.length - 1) {
      this.history = this.history.slice(0, this.currentIndex + 1);
    }

    // Add new state
    this.history.push({
      machineIR: JSON.parse(JSON.stringify(machineIR)), // Deep clone
      description,
      timestamp: Date.now()
    });

    // Trim history if too long
    if (this.history.length > MAX_HISTORY_SIZE) {
      this.history = this.history.slice(this.history.length - MAX_HISTORY_SIZE);
    }

    this.currentIndex = this.history.length - 1;
    this.persist();

    return this.getStatus();
  }

  /**
   * Undo the last change
   * @returns {Object|null} The previous machine IR state, or null if at beginning
   */
  undo() {
    if (!this.canUndo()) return null;

    this.currentIndex--;
    this.persist();

    return {
      machineIR: JSON.parse(JSON.stringify(this.history[this.currentIndex].machineIR)),
      description: this.history[this.currentIndex].description,
      status: this.getStatus()
    };
  }

  /**
   * Redo a previously undone change
   * @returns {Object|null} The next machine IR state, or null if at end
   */
  redo() {
    if (!this.canRedo()) return null;

    this.currentIndex++;
    this.persist();

    return {
      machineIR: JSON.parse(JSON.stringify(this.history[this.currentIndex].machineIR)),
      description: this.history[this.currentIndex].description,
      status: this.getStatus()
    };
  }

  /**
   * Check if undo is available
   */
  canUndo() {
    return this.currentIndex > 0;
  }

  /**
   * Check if redo is available
   */
  canRedo() {
    return this.currentIndex < this.history.length - 1;
  }

  /**
   * Get current status
   */
  getStatus() {
    return {
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      historyLength: this.history.length,
      currentIndex: this.currentIndex,
      undoDescription: this.canUndo() ? this.history[this.currentIndex - 1]?.description : null,
      redoDescription: this.canRedo() ? this.history[this.currentIndex + 1]?.description : null,
      hasUnsavedChanges: this.currentIndex !== this.lastSavedIndex
    };
  }

  /**
   * Mark current state as saved
   */
  markSaved() {
    this.lastSavedIndex = this.currentIndex;
    this.persist();
  }

  /**
   * Clear all history
   */
  clear() {
    this.history = [];
    this.currentIndex = -1;
    this.lastSavedIndex = -1;
    this.persist();
  }

  /**
   * Persist to localStorage
   */
  persist() {
    try {
      const data = {
        machineId: this.machineId,
        history: this.history,
        currentIndex: this.currentIndex,
        lastSavedIndex: this.lastSavedIndex
      };
      localStorage.setItem(`${STORAGE_KEY}_${this.machineId}`, JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to persist machine history', e);
    }
  }

  /**
   * Restore from localStorage
   */
  restore() {
    try {
      const stored = localStorage.getItem(`${STORAGE_KEY}_${this.machineId}`);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.machineId === this.machineId) {
          this.history = data.history || [];
          this.currentIndex = data.currentIndex ?? -1;
          this.lastSavedIndex = data.lastSavedIndex ?? -1;
        }
      }
    } catch (e) {
      console.warn('Failed to restore machine history', e);
    }
  }

  /**
   * Get the current machine IR from history
   */
  getCurrentState() {
    if (this.currentIndex >= 0 && this.currentIndex < this.history.length) {
      return JSON.parse(JSON.stringify(this.history[this.currentIndex].machineIR));
    }
    return null;
  }
}

export default MachineHistoryManager;
